
import {
  type PosthogExceptionEntry,
  exceptionValue,
} from "@/lib/error-tracking/noise-filters";

type FrameFileFields = {
  filename?: string;
  abs_path?: string;
};

const RRWEB_RECORDER_FILES = [
  "rrweb.record",
  "rrweb-record",
  "rrweb.record.dist",
  "posthog-recorder",
] as const;

function frameFile(frame: FrameFileFields): string {
  return frame.filename ?? frame.abs_path ?? "";
}

function isRrwebRecorderFrame(frame: FrameFileFields): boolean {
  const file = frameFile(frame);
  return RRWEB_RECORDER_FILES.some((needle) => file.includes(needle));
}

function hasRrwebRecorderFrame(frames: Array<FrameFileFields>): boolean {
  return frames.some(isRrwebRecorderFrame);
}

function isRemoveEventListenerFailure(value: string): boolean {
  return value.includes("removeEventListener is not a function");
}

export function isPostHogRrwebRecorderNoise(
  first: PosthogExceptionEntry,
): boolean {
  if (!isRemoveEventListenerFailure(exceptionValue(first))) return false;
  return hasRrwebRecorderFrame(first.stacktrace?.frames ?? []);
}

const BARE_FETCH_FAILURE_VALUES = ["Failed to fetch", "Load failed"];

const POSTHOG_FRAME_MARKERS = [
  "extensions/replay",
  "rrweb-plugins",
  "posthog-js",
  "posthog-recorder",
];

function isPostHogOwnedFrame(frame: FrameFileFields): boolean {
  const file = frameFile(frame);
  if (!file) return false;
  return POSTHOG_FRAME_MARKERS.some((marker) => file.includes(marker));
}

function isPostHogOwnedFetchEntry(entry: PosthogExceptionEntry): boolean {
  if (!BARE_FETCH_FAILURE_VALUES.includes(exceptionValue(entry).trim())) {
    return false;
  }
  const frames = entry.stacktrace?.frames ?? [];
  if (frames.length === 0) return false;
  return frames.every(isPostHogOwnedFrame);
}

export function isPostHogPatchedFetchFailureNoise(
  list: Array<PosthogExceptionEntry>,
): boolean {
  return list.length > 0 && list.every(isPostHogOwnedFetchEntry);
}

export function isDomEventCapturedAsExceptionNoise(
  first: PosthogExceptionEntry,
): boolean {
  const type = first.type ?? first.$exception_type ?? "";
  if (type !== "Event" && type !== "CustomEvent") return false;
  if (
    exceptionValue(first) !==
    `${type} captured as exception with keys: isTrusted`
  ) {
    return false;
  }
  return (first.stacktrace?.frames ?? []).length === 0;
}
